import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, X } from 'lucide-react';
import { apiClient } from '@/lib/api';
import './fleet-explorer.css';

// Column order of each day tuple returned by /fleet-explorer/daily.
const IDX = {
  start: 0, end: 1, km: 2, drive: 3, stop: 4, park: 5, maxspd: 6, avgspd: 7,
  liters: 8, sar: 9, fills: 10, stops: 11, pos: 12, src: 13, first: 14, last: 15,
  engine: 16, idle: 17,
} as const;

type Cell = number | string | null;
interface Truck {
  id: string; plate: string; type: string | null; model: string | null;
  driver: string | null; km_rate: number; days: Record<string, Cell[]>;
}
interface DailyResp { generated_at: string; order: string[]; trucks: Truck[]; }
interface DayDetail {
  pings: [number, number, number, number, (number | null), (number | null)][];
  stops: [string, string, number, number, number, (string | null)][];
  fuel: [string, number, number, (number | null), string, (string | null)][];
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const heading = (deg: number | null): string => (deg == null ? '' : COMPASS[Math.round(((deg % 360) / 45)) % 8]);
function haversine(a: [number, number, number, number, (number | null), (number | null)], b: [number, number, number, number, (number | null), (number | null)]): number {
  const R = 6371, toR = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toR, dLng = (b[2] - a[2]) * toR;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * toR) * Math.cos(b[1] * toR) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const num = (v: Cell): number | null => (v == null || v === '' ? null : Number(v));
const fmt = (v: Cell, d = 0): string => {
  const n = num(v);
  return n == null ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const hms = (v: Cell): string => {
  const s = num(v);
  if (s == null) return '—';
  const h = Math.floor(s / 3600); const m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const monLabel = (m: string | null) => { if (!m) return '—'; const [y, mo] = m.split('-'); return `${MON[+mo - 1]} ${y}`; };
const daysInMonth = (m: string | null) => { if (!m) return 0; const [y, mo] = m.split('-').map(Number); return new Date(y, mo, 0).getDate(); };

function csv(rows: (string | number | null)[][]): string {
  return rows.map((r) => r.map((c) => {
    const s = c == null ? '' : String(c);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\n');
}
function dl(name: string, text: string, type: string) {
  const b = new Blob([text], { type }); const u = URL.createObjectURL(b);
  const a = document.createElement('a'); a.href = u; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
}
const dlJson = (name: string, obj: unknown) => dl(name, JSON.stringify(obj, null, 2), 'application/json');

const DAILY_COLS = ['plate', 'date', 'start_odo', 'end_odo', 'km', 'drive_min', 'stop_min', 'max_kmh', 'avg_kmh', 'diesel_L', 'diesel_SAR', 'fills', 'stops', 'pings', 'odo_src', 'first', 'last', 'engine_min', 'idle_min'];
function dailyRow(plate: string, d: string, r: Cell[]): (string | number | null)[] {
  return [plate, d, num(r[IDX.start]), num(r[IDX.end]), num(r[IDX.km]),
    num(r[IDX.drive]) != null ? Math.round(Number(r[IDX.drive]) / 60) : null,
    num(r[IDX.stop]) != null ? Math.round(Number(r[IDX.stop]) / 60) : null,
    num(r[IDX.maxspd]), num(r[IDX.avgspd]), num(r[IDX.liters]), num(r[IDX.sar]),
    num(r[IDX.fills]), num(r[IDX.stops]), num(r[IDX.pos]),
    (r[IDX.src] as string) ?? '', (r[IDX.first] as string) ?? '', (r[IDX.last] as string) ?? '',
    num(r[IDX.engine]) != null ? Math.round(Number(r[IDX.engine]) / 60) : null,
    num(r[IDX.idle]) != null ? Math.round(Number(r[IDX.idle]) / 60) : null];
}

function Spark({ vals }: { vals: number[] }) {
  if (!vals.length) return null;
  const w = 54, h = 18, mx = Math.max(...vals, 1), step = w / Math.max(vals.length - 1, 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(h - (v / mx) * (h - 2) - 1).toFixed(1)}`).join(' ');
  return (
    <svg className="spark" width={w} height={h} aria-hidden="true">
      <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default function FleetExplorerPage() {
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plate, setPlate] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [sortK, setSortK] = useState<string>('date');
  const [sortDir, setSortDir] = useState(-1);
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [dayData, setDayData] = useState<DayDetail | null>(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [rawBusy, setRawBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient.get<DailyResp>('/api/v1/fleet-explorer/daily')
      .then((r) => { if (!cancelled) setTrucks(r.data.trucks || []); })
      .catch(() => { if (!cancelled) setError('Could not load fleet data.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const byPlate = useMemo(() => Object.fromEntries(trucks.map((t) => [t.plate, t])), [trucks]);
  const truck = plate ? byPlate[plate] : null;

  const recentKm = useCallback((t: Truck): number[] => {
    return Object.keys(t.days).sort().slice(-14).map((d) => num(t.days[d][IDX.km]) ?? 0);
  }, []);

  const railRows = useMemo(() => {
    const s = q.toLowerCase();
    return trucks
      .filter((t) => !s || t.plate.toLowerCase().includes(s) || (t.driver || '').toLowerCase().includes(s))
      .map((t) => ({ t, wk: recentKm(t).reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.wk - a.wk);
  }, [trucks, q, recentKm]);

  const selectTruck = useCallback((p: string) => {
    const t = byPlate[p]; if (!t) return;
    const months = Object.keys(t.days).map((d) => d.slice(0, 7)).sort();
    setPlate(p);
    setMonth(months.length ? months[months.length - 1] : null);
    setDayKey(null); setDayData(null);
    window.scrollTo(0, 0);
  }, [byPlate]);

  const months = useMemo(() => (truck ? [...new Set(Object.keys(truck.days).map((d) => d.slice(0, 7)))].sort() : []), [truck]);
  const shiftMonth = (dir: number) => {
    if (!month) return;
    const i = months.indexOf(month);
    const ni = Math.max(0, Math.min(months.length - 1, i + dir));
    setMonth(months[ni]);
  };

  const agg = useMemo(() => {
    if (!truck) return null;
    let km = 0, l = 0, sar = 0, days = 0, drive = 0, engine = 0, idle = 0, lastOdo: number | null = null, lastSrc = '';
    Object.keys(truck.days).sort().forEach((d) => {
      const r = truck.days[d];
      if (month && d.slice(0, 7) !== month) return;
      if (num(r[IDX.km]) != null) { km += Number(r[IDX.km]); days++; }
      if (num(r[IDX.liters]) != null) l += Number(r[IDX.liters]);
      if (num(r[IDX.sar]) != null) sar += Number(r[IDX.sar]);
      if (num(r[IDX.drive]) != null) drive += Number(r[IDX.drive]);
      if (num(r[IDX.engine]) != null) engine += Number(r[IDX.engine]);
      if (num(r[IDX.idle]) != null) idle += Number(r[IDX.idle]);
    });
    Object.keys(truck.days).sort().forEach((d) => {
      const r = truck.days[d];
      if (num(r[IDX.end]) != null) { lastOdo = Number(r[IDX.end]); lastSrc = (r[IDX.src] as string) || ''; }
    });
    const rate = truck.km_rate ?? 0.85;
    const gross = km * rate;             // salary earned on distance
    const net = gross - sar;             // diesel is paid out of the per-km rate
    return { km, l, sar, days, drive, engine, idle, lastOdo, lastSrc, rate, gross, net };
  }, [truck, month]);

  const monthRows = useMemo(() => {
    if (!truck || !month) return [];
    const rows = Object.keys(truck.days).filter((d) => d.slice(0, 7) === month).map((d) => ({ d, r: truck.days[d] }));
    rows.sort((a, b) => {
      if (sortK === 'date') return sortDir * a.d.localeCompare(b.d);
      const k = IDX[sortK as keyof typeof IDX];
      const x = num(a.r[k]) ?? -1, y = num(b.r[k]) ?? -1;
      return sortDir * (x - y);
    });
    return rows;
  }, [truck, month, sortK, sortDir]);

  const openDay = useCallback((key: string) => {
    if (!truck) return;
    setDayKey(key); setDayData(null); setDayLoading(true);
    apiClient.get<DayDetail>(`/api/v1/fleet-explorer/day/${truck.id}/${key}`)
      .then((r) => setDayData(r.data))
      .catch(() => setDayData({ pings: [], stops: [], fuel: [] }))
      .finally(() => setDayLoading(false));
  }, [truck]);
  const closeDay = () => { setDayKey(null); setDayData(null); };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDay(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const setSort = (k: string) => {
    if (k === sortK) setSortDir(-sortDir); else { setSortK(k); setSortDir(-1); }
  };

  // downloads
  const downloadAllCsv = () => {
    const rows: (string | number | null)[][] = [DAILY_COLS];
    trucks.forEach((t) => Object.keys(t.days).sort().forEach((d) => rows.push(dailyRow(t.plate, d, t.days[d]))));
    dl('jasr-fleet-all-daily.csv', csv(rows), 'text/csv');
  };
  const downloadAllJson = () => dlJson('jasr-fleet-all-daily.json', { trucks });
  const downloadTruckCsv = () => {
    if (!truck) return;
    const rows: (string | number | null)[][] = [DAILY_COLS];
    Object.keys(truck.days).sort().forEach((d) => rows.push(dailyRow(truck.plate, d, truck.days[d])));
    dl(`${truck.plate}-daily.csv`, csv(rows), 'text/csv');
  };
  const downloadTruckJson = () => { if (truck) dlJson(`${truck.plate}-daily.json`, truck); };
  const downloadDayCsv = () => {
    if (!truck || !dayKey || !dayData) return;
    const rows: (string | number | null)[][] = [['type', 'time', 'lat', 'lng', 'speed_kmh', 'detail']];
    dayData.pings.forEach((p) => rows.push(['ping', new Date(p[0] * 1000).toISOString(), p[1], p[2], p[3], `${heading(p[4])}${p[5] != null ? ` engine ${p[5] ? 'on' : 'off'}` : ''}`]));
    dayData.stops.forEach((s) => rows.push(['stop', `${s[0]}-${s[1]}`, s[3], s[4], '', `${s[2]}min ${s[5] || ''}`]));
    dayData.fuel.forEach((f) => rows.push(['fuel', f[0], '', '', '', `${f[1]}L ${f[2]}SAR @${f[3]}km ${f[4]}`]));
    dl(`${truck.plate}-${dayKey}-raw.csv`, csv(rows), 'text/csv');
  };
  const downloadDayJson = () => { if (truck && dayKey && dayData) dlJson(`${truck.plate}-${dayKey}.json`, { plate: truck.plate, date: dayKey, ...dayData }); };
  const downloadRaw = async (from: string, to: string, fname: string) => {
    if (!truck || rawBusy) return;
    setRawBusy(true);
    try {
      const r = await apiClient.get(`/api/v1/fleet-explorer/export/${truck.id}`, { params: { from, to }, responseType: 'text' });
      const text = typeof r.data === 'string' ? r.data : JSON.stringify(r.data);
      dl(fname, text, 'application/json');
    } catch {
      alert('Could not build the raw export. Try a smaller range (max 40 days).');
    } finally {
      setRawBusy(false);
    }
  };
  const monthEnd = (m: string) => `${m}-${String(daysInMonth(m)).padStart(2, '0')}`;

  // calendar cells for current month
  const calCells = useMemo(() => {
    if (!truck || !month) return null;
    const [y, mo] = month.split('-').map(Number);
    const startDow = (new Date(y, mo - 1, 1).getDay() + 6) % 7; // Mon=0
    const dim = daysInMonth(month);
    const kmVals: (number | null)[] = [];
    for (let dd = 1; dd <= dim; dd++) {
      const key = `${month}-${String(dd).padStart(2, '0')}`;
      const r = truck.days[key];
      kmVals.push(r && num(r[IDX.km]) != null ? Number(r[IDX.km]) : null);
    }
    const mx = Math.max(...kmVals.filter((v): v is number => v != null), 1);
    return { startDow, dim, mx };
  }, [truck, month]);

  const dr = dayKey && truck ? truck.days[dayKey] : null;

  return (
    <div className="fx">
      <div className="fxroot">
        <div className="top">
          <Link to="/home" className="btn" style={{ padding: '7px 10px' }} aria-label="Home"><ArrowLeft size={15} /></Link>
          <div className="logo"><span className="e">JASR Hub</span><span className="t">Fleet Data Explorer</span></div>
          <div className="spacer" />
          <button className="btn" onClick={downloadAllCsv}><Download size={14} /> All data · CSV</button>
          <button className="btn primary" onClick={downloadAllJson}><Download size={14} /> All data · JSON</button>
        </div>

        {loading ? (
          <div className="loading">Loading fleet data…</div>
        ) : error ? (
          <div className="loading">{error}</div>
        ) : (
          <div className="app">
            <aside className={`rail ${plate ? 'hide-m' : ''}`}>
              <div className="search">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search truck or driver…" />
              </div>
              <div className="list">
                {railRows.length === 0 && <div className="none" style={{ margin: 16 }}>No trucks match “{q}”.</div>}
                {railRows.map(({ t, wk }) => (
                  <button key={t.plate} type="button" className={`tk ${t.plate === plate ? 'on' : ''}`} onClick={() => selectTruck(t.plate)}>
                    <span><span className="pl">{t.plate}</span><br /><span className="mt">{(t.type || '').replace(/_/g, ' ')}</span></span>
                    <Spark vals={recentKm(t)} />
                    <span className="kmw"><b className="tnum">{fmt(wk)}</b>km · 14d</span>
                  </button>
                ))}
              </div>
            </aside>

            <main className="detail">
              {!truck || !agg ? (
                <div className="empty">
                  <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 13h13V6H3zM16 8h3l2 3v2h-5z" /><circle cx="7" cy="17" r="2" /><circle cx="18" cy="17" r="2" /></svg>
                  <div>Select a truck to explore its full history<br />km, meters, diesel, stops and every raw GPS ping</div>
                </div>
              ) : (
                <>
                  <button type="button" className="btn back" onClick={() => { setPlate(null); }}><ArrowLeft size={15} /> Trucks</button>
                  <div className="dhead">
                    <div>
                      <h1>{truck.plate}</h1>
                      <div className="meta">
                        {(truck.type || '').replace(/_/g, ' ')}
                        {truck.model ? <> · <b>{truck.model}</b></> : null}
                        {truck.driver ? <> · driver <b>{truck.driver}</b></> : null}
                      </div>
                      {agg.lastOdo != null && agg.lastOdo > 2000000 && (
                        <div className="flags"><span className="flag bad">odometer entry looks wrong</span></div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn" onClick={downloadTruckCsv}><Download size={14} /> Truck · CSV</button>
                      <button className="btn" onClick={downloadTruckJson}><Download size={14} /> Truck · JSON</button>
                      {month && <button className="btn primary" disabled={rawBusy} onClick={() => downloadRaw(`${month}-01`, monthEnd(month), `${truck.plate}-${month}-raw.json`)}><Download size={14} /> {rawBusy ? 'Building…' : 'Full raw · month'}</button>}
                    </div>
                  </div>

                  <div className="stat-row">
                    <div className="stat"><div className="k">Current odometer</div><div className="v tnum">{fmt(agg.lastOdo)}<span className="u">km {agg.lastSrc === 'pump' ? '· pump' : agg.lastSrc === 'computed' ? '· est' : ''}</span></div></div>
                    <div className="stat"><div className="k">Km · {monLabel(month)}</div><div className="v tnum">{fmt(agg.km)}<span className="u">km</span></div></div>
                    <div className="stat"><div className="k">Diesel · month</div><div className="v tnum">{fmt(agg.l)}<span className="u">L</span></div></div>
                    <div className="stat"><div className="k">Cost · month</div><div className="v tnum">{fmt(agg.sar)}<span className="u">SAR</span></div></div>
                    <div className="stat"><div className="k">Active days</div><div className="v tnum">{agg.days}<span className="u">/ {daysInMonth(month)}</span></div></div>
                  </div>

                  <div className="stat-row" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
                    <div className="stat"><div className="k">Engine on · month</div><div className="v tnum">{fmt(agg.engine / 3600)}<span className="u">h</span></div></div>
                    <div className="stat"><div className="k">Idle (engine on, parked)</div><div className="v tnum">{fmt(agg.idle / 3600)}<span className="u">h</span></div></div>
                    <div className="stat"><div className="k">Salary @ {agg.rate} SAR/km</div><div className="v tnum">{fmt(agg.gross)}<span className="u">SAR gross</span></div></div>
                    <div className="stat"><div className="k">Diesel (in salary)</div><div className="v tnum">{fmt(agg.sar)}<span className="u">SAR</span></div></div>
                    <div className="stat"><div className="k">Net salary to pay</div><div className="v tnum" style={{ color: agg.net >= 0 ? 'var(--good)' : 'var(--bad)' }}>{fmt(agg.net)}<span className="u">SAR</span></div></div>
                  </div>
                  <div className="banner" style={{ marginTop: 10 }}>
                    Salary estimate: <b>{fmt(agg.km)} km × {agg.rate} SAR/km = {fmt(agg.gross)} SAR</b>, diesel of {fmt(agg.sar)} SAR is paid from it, so net ≈ <b>{fmt(agg.net)} SAR</b> for {monLabel(month)}. Rate is 0.85 SAR/km unless a per-driver rate is set. Informational only.
                  </div>

                  <div className="panel">
                    <div className="ph">
                      <div><h2>Daily distance calendar</h2><div className="sub">colour = km driven · click a day for the raw drill-down</div></div>
                      <div className="cal-nav">
                        <button onClick={() => shiftMonth(-1)} aria-label="previous month">‹</button>
                        <div className="m">{monLabel(month)}</div>
                        <button onClick={() => shiftMonth(1)} aria-label="next month">›</button>
                      </div>
                    </div>
                    <div className="pb">
                      <div className="cal">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="dow">{d}</div>)}
                        {calCells && Array.from({ length: calCells.startDow }).map((_, i) => <div key={`b${i}`} className="cell blank" />)}
                        {calCells && Array.from({ length: calCells.dim }).map((_, i) => {
                          const dd = i + 1;
                          const key = `${month}-${String(dd).padStart(2, '0')}`;
                          const r = truck.days[key];
                          const km = r && num(r[IDX.km]) != null ? Number(r[IDX.km]) : null;
                          const alpha = km == null ? 0 : (0.12 + 0.73 * (km / calCells.mx));
                          const bg = km == null ? 'var(--surface-2)' : `rgba(var(--heat),${alpha.toFixed(2)})`;
                          return (
                            <button type="button" key={key} className={`cell ${r == null ? 'nodata' : ''} ${dayKey === key ? 'on' : ''}`} style={{ background: bg }} onClick={() => openDay(key)}>
                              <div className="dn">{dd}</div>
                              <div className="km tnum">{km == null ? (r ? '0' : '·') : fmt(km)}{km != null ? <small>km</small> : null}</div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="cal-legend">less
                        <i style={{ background: 'rgba(var(--heat),.12)' }} /><i style={{ background: 'rgba(var(--heat),.35)' }} />
                        <i style={{ background: 'rgba(var(--heat),.6)' }} /><i style={{ background: 'rgba(var(--heat),.85)' }} /> more</div>
                    </div>
                  </div>

                  <div className="panel">
                    <div className="ph"><div><h2>Daily log — {monLabel(month)}</h2><div className="sub">start meter → end meter · click a row for everything that day</div></div></div>
                    <div className="scroll">
                      <table>
                        <thead><tr>
                          <th className="l" onClick={() => setSort('date')}>Date</th>
                          <th onClick={() => setSort('start')}>Start meter</th>
                          <th onClick={() => setSort('end')}>End meter</th>
                          <th onClick={() => setSort('km')}>Km</th>
                          <th onClick={() => setSort('drive')}>Drive</th>
                          <th onClick={() => setSort('maxspd')}>Max km/h</th>
                          <th onClick={() => setSort('liters')}>Diesel L</th>
                          <th onClick={() => setSort('fills')}>Fills</th>
                          <th onClick={() => setSort('stops')}>Stops</th>
                          <th onClick={() => setSort('pos')}>Pings</th>
                          <th onClick={() => setSort('engine')}>Engine h</th>
                          <th onClick={() => setSort('idle')}>Idle h</th>
                        </tr></thead>
                        <tbody>
                          {monthRows.length === 0 && <tr><td colSpan={12} className="dim" style={{ textAlign: 'center', padding: 20 }}>No activity recorded this month.</td></tr>}
                          {monthRows.map(({ d, r }) => (
                            <tr key={d} className={dayKey === d ? 'on' : ''} onClick={() => openDay(d)}>
                              <td className="l"><b>{+d.slice(-2)} {MON[+d.slice(5, 7) - 1]}</b></td>
                              <td className="tnum dim">{fmt(r[IDX.start])}</td>
                              <td className="tnum">{num(r[IDX.end]) != null ? fmt(r[IDX.end]) : <span className="dim">—</span>} {r[IDX.src] === 'pump' ? <span className="src pump">pump</span> : r[IDX.src] === 'computed' ? <span className="src">est</span> : null}</td>
                              <td className="tnum" style={{ fontWeight: 700 }}>{fmt(r[IDX.km])}</td>
                              <td className="tnum dim">{hms(r[IDX.drive])}</td>
                              <td className="tnum">{num(r[IDX.maxspd]) != null ? fmt(r[IDX.maxspd]) : '—'}</td>
                              <td className="tnum">{num(r[IDX.liters]) != null ? fmt(r[IDX.liters]) : <span className="dim">—</span>}</td>
                              <td className={`tnum ${num(r[IDX.fills]) ? '' : 'dim'}`}>{num(r[IDX.fills]) || '0'}</td>
                              <td className="tnum dim">{num(r[IDX.stops]) ?? '—'}</td>
                              <td className="tnum dim">{num(r[IDX.pos]) ?? '—'}</td>
                              <td className="tnum dim">{num(r[IDX.engine]) != null ? fmt(Number(r[IDX.engine]) / 3600, 1) : '—'}</td>
                              <td className="tnum" style={{ color: num(r[IDX.idle]) != null && Number(r[IDX.idle]) / 3600 >= 4 ? 'var(--warn)' : undefined }}>{num(r[IDX.idle]) != null ? fmt(Number(r[IDX.idle]) / 3600, 1) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="banner">Raw GPS pings load on demand when you open a day — location and speed at each moment. Afaqy is sampled every ~15 min today (≈96 points/day); the every-2-3-min upgrade will make it far denser.</div>
                </>
              )}
            </main>
          </div>
        )}

        {/* day drawer */}
        <div className={`drawer-bg ${dayKey ? 'open' : ''}`} onClick={closeDay} />
        <div className={`drawer ${dayKey ? 'open' : ''}`} aria-hidden={!dayKey}>
          {dayKey && truck && (
            <>
              <div className="dh">
                <div>
                  <h3>{truck.plate}</h3>
                  <div className="d">{new Date(`${dayKey}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
                </div>
                <button className="x" onClick={closeDay} aria-label="close"><X size={17} /></button>
              </div>
              <div className="db">
                <div className="mini">
                  <div className="m"><div className="k">Start meter</div><div className="v tnum">{fmt(dr?.[IDX.start] ?? null)}</div></div>
                  <div className="m"><div className="k">End meter</div><div className="v tnum">{fmt(dr?.[IDX.end] ?? null)}</div></div>
                  <div className="m"><div className="k">Km driven</div><div className="v tnum">{fmt(dr?.[IDX.km] ?? null)}</div></div>
                  <div className="m"><div className="k">Drive time</div><div className="v tnum">{hms(dr?.[IDX.drive] ?? null)}</div></div>
                  <div className="m"><div className="k">Max speed</div><div className="v tnum">{fmt(dr?.[IDX.maxspd] ?? null)}<span style={{ fontSize: 10 }}> km/h</span></div></div>
                  <div className="m"><div className="k">Engine on</div><div className="v tnum">{dr && num(dr[IDX.engine]) != null ? fmt(Number(dr[IDX.engine]) / 3600, 1) : '—'}<span style={{ fontSize: 10 }}> h</span></div></div>
                  <div className="m"><div className="k">Idle (on, parked)</div><div className="v tnum">{dr && num(dr[IDX.idle]) != null ? fmt(Number(dr[IDX.idle]) / 3600, 1) : '—'}<span style={{ fontSize: 10 }}> h</span></div></div>
                  <div className="m"><div className="k">First → last</div><div className="v" style={{ fontSize: 13 }}>{(dr?.[IDX.first] as string) || '—'} → {(dr?.[IDX.last] as string) || '—'}</div></div>
                </div>

                {dayLoading ? <div className="none" style={{ marginTop: 16 }}>Loading raw data…</div> : dayData && (
                  <>
                    <div className="subhead">Fuel fills <span className="cnt">{dayData.fuel.length}</span></div>
                    {dayData.fuel.length ? dayData.fuel.map((f, i) => (
                      <div className="fuelrow" key={i}>
                        <div><b className="tnum">{fmt(f[1], 1)} L</b> <span className="prov">{f[4]}</span><div className="dim" style={{ fontSize: 11 }}>{f[5] || ''} · {f[0]}</div></div>
                        <div className="r"><div className="tnum">{fmt(f[2])} SAR</div><div className="dim tnum" style={{ fontSize: 11 }}>@ {fmt(f[3])} km</div></div>
                      </div>
                    )) : <div className="none">No diesel fills this day.</div>}

                    <div className="subhead">Stops <span className="cnt">{dayData.stops.length} (≥3 min)</span></div>
                    {dayData.stops.length ? dayData.stops.map((s, i) => (
                      <div className="stoprow" key={i}>
                        <div><b>{s[0]} → {s[1]}</b><div className="dim" style={{ fontSize: 11 }}>{s[5] || 'stop'}</div></div>
                        <div className="r"><div className="tnum">{s[2]} min</div><div className="dim" style={{ fontSize: 11 }}><a href={`https://maps.google.com/?q=${s[3]},${s[4]}`} target="_blank" rel="noopener noreferrer">{s[3]}, {s[4]}</a></div></div>
                      </div>
                    )) : <div className="none">No stops ≥3 min recorded this day.</div>}

                    <div className="subhead">Raw GPS pings <span className="cnt">{dayData.pings.length} points{dayData.pings.length ? ` · peak ${fmt(dayData.pings.reduce((a, p) => Math.max(a, p[3]), 0))} km/h` : ''}</span></div>
                    {dayData.pings.length ? (
                      <div className="rawlist">
                        {dayData.pings.map((p, i) => {
                          const prev = i > 0 ? dayData.pings[i - 1] : null;
                          const moved = prev ? haversine(prev, p) : 0;
                          const dir = heading(p[4]);
                          const eng = p[5];
                          return (
                            <div className="rawrow" key={i}>
                              <span className="t">{new Date(p[0] * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' })}</span>
                              <span className="c">
                                <a href={`https://maps.google.com/?q=${p[1]},${p[2]}`} target="_blank" rel="noopener noreferrer">{p[1]}, {p[2]}</a>
                                <div style={{ fontSize: 10, color: 'var(--faint)', fontFamily: 'var(--sans)' }}>
                                  {dir && <>heading {dir}</>}
                                  {moved >= 0.05 ? ` · moved ${fmt(moved, 1)} km` : ''}
                                  {eng != null ? ` · engine ${eng ? 'on' : 'off'}` : ''}
                                </div>
                              </span>
                              <span className={`s ${p[3] > 0 ? 'mv' : 'dim'}`}>{fmt(p[3])} <small style={{ fontSize: 9 }}>km/h</small></span>
                            </div>
                          );
                        })}
                      </div>
                    ) : <div className="none">No raw pings recorded for this day (this truck may not be Afaqy-tracked).</div>}
                  </>
                )}
              </div>
              <div className="foot">
                <button className="btn" onClick={downloadDayCsv}><Download size={14} /> Day · CSV</button>
                <button className="btn" onClick={downloadDayJson}><Download size={14} /> Day · JSON</button>
                <button className="btn primary" disabled={rawBusy} onClick={() => dayKey && downloadRaw(dayKey, dayKey, `${truck.plate}-${dayKey}-raw.json`)}><Download size={14} /> {rawBusy ? 'Building…' : 'Full raw'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
